/**
 * R0003 Training Hours — week-lock permission helper.
 *
 * Determines whether the current user may add, edit, or delete training-hours
 * records for the selected week. Wired into the records `RDTable` in
 * `R0003Training.js` so the Add button and per-row edit/delete icons are
 * hidden for users who cannot edit the selected week.
 */

import { isAfter, parseISO, startOfDay } from 'date-fns';

/**
 * Phase 1 hardcoded list of roles that bypass the training-hours week lock.
 *
 * Phase 2 will replace this constant with a value loaded from the
 * `grm.reportadmins` DynamoDB table (`report: 'R0003'`,
 * `itemType: 'trainingEditRoles'`) and threaded down from `R0003Main.js`
 * exactly like the existing `thresholds` prop. To keep the Phase 2 diff
 * additive, `canEditTrainingHours` should evolve to accept an explicit
 * `editRoles` argument defaulting to `TRAINING_EDIT_ROLES`; call sites in
 * `R0003Training.js` should not import this constant directly once a
 * loaded value is available.
 *
 * @type {ReadonlyArray<string>}
 */
export const TRAINING_EDIT_ROLES = ['Admin', 'AreaMgr'];

/**
 * Determines whether the current user may add, edit, or delete training-hours
 * records for the given week.
 *
 * Roles in `TRAINING_EDIT_ROLES` (currently Admin and AreaMgr) bypass the
 * lock for any week. For all other roles, editing is permitted only when the
 * week's Monday is strictly after the start of `today` — i.e., the upcoming
 * (future-Monday) week is editable while the current week and all prior
 * weeks are locked.
 *
 * The `today` argument is exposed so unit tests can pin date logic
 * deterministically without mocking the system clock.
 *
 * @param {Object} user - Authenticated user from `useUser()`.
 * @param {string} user.role - Role string (e.g., 'Admin', 'AreaMgr', 'GenMgr').
 * @param {string} weekStartDate - Monday of the selected week as a `yyyy-MM-dd`
 *   string, as produced by `generateWeeks` in `R0003Training.js`.
 * @param {Date} [today=new Date()] - Reference "now" used to evaluate whether
 *   the selected week has started.
 * @returns {boolean} `true` if the user may mutate training-hours records for
 *   the given week; `false` otherwise (including when `user`, `user.role`, or
 *   `weekStartDate` is missing for a non-privileged role).
 */
export function canEditTrainingHours(user, weekStartDate, today = new Date()) {
  if (!user || !user.role) {
    return false;
  }

  if (TRAINING_EDIT_ROLES.includes(user.role)) {
    return true;
  }

  if (!weekStartDate) {
    return false;
  }

  const weekMonday = parseISO(weekStartDate);
  return isAfter(weekMonday, startOfDay(today));
}
